import { removeVisitaOfflineOperation, updateVisitaOfflineOperationStatus } from './visitaOfflineOperationQueue.js';
import { getOfflineStorageDriver } from './offlineStorageAdapter.js';

export async function reconcileStoredFinalization({ operation, reviewedVersion, send, isCurrent = () => true }) {
    const payload = typeof operation.payload === 'string' ? JSON.parse(operation.payload) : operation.payload;
    const uuid = operation.operation_uuid || operation.operationUuid || operation.id;
    const body = JSON.stringify({ ...payload, client_uuid: uuid });
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(body));
    const hash = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
    if (!isCurrent()) return { status: 'stale' };
    const response = await send({ operation_uuid: uuid, payload_hash: hash, reviewed_version: reviewedVersion });
    if (!isCurrent()) return { status: 'stale' };
    return response?.data || response;
}

// Una respuesta incierta conserva la intención original. Conciliar y crear una
// sucesora es un flujo distinto; nunca se cambia la clave o el cuerpo al reenviar.
export async function sendClaimedVisitaFinalization({ claimed, ownerId, empresaId, send, isCurrent = () => true }) {
    if (claimed?.type !== 'finalizar_visita' || !claimed.claimToken || claimed.ownerId !== ownerId) {
        throw new Error('La finalización requiere una concesión propia.');
    }
    const update = async (status, message, extra = {}) => {
        const saved = await updateVisitaOfflineOperationStatus(claimed.id, {
            status, lastError: message, ownerId, claimToken: claimed.claimToken, ...extra,
        });
        return saved ? { status, message, ...extra } : { status: 'stale' };
    };
    let payload;
    try {
        payload = typeof claimed.payload === 'string' ? JSON.parse(claimed.payload) : structuredClone(claimed.payload);
        if (!payload || typeof payload !== 'object' || Array.isArray(payload)) throw new Error('payload');
    } catch {
        return update('conflict', 'No se puede leer la intención de cierre. Datos conservados para revisión.');
    }
    const uuid = claimed.operation_uuid || claimed.operationUuid || claimed.id;
    if (!uuid || (claimed.operation_uuid && claimed.operationUuid && claimed.operation_uuid !== claimed.operationUuid)) {
        return update('conflict', 'La identidad del cierre requiere revisión.');
    }
    if (payload.finalization_contract === 2 && empresaId !== undefined && Number(payload.owner_empresa_id) !== Number(empresaId)) {
        return update('conflict', 'Este cierre pertenece a otro contexto de empresa. Se conserva sin enviarlo.');
    }
    if (!isCurrent()) return { status: 'stale' };

    let response;
    try {
        response = await send(payload, { idempotencyKey: `operation:${uuid}`, client_uuid: uuid });
    } catch (error) {
        if (!isCurrent()) return { status: 'stale' };
        const httpStatus = error?.response?.status;
        const data = error?.response?.data || {};
        if (httpStatus === 401) {
            return update('pending', 'Sesión expirada. El cierre se conserva para reautenticar.', { unauthorized: true });
        }
        if (!error?.response || [408, 423, 429].includes(httpStatus) || httpStatus >= 500
            || (httpStatus === 409 && data.operation_status === 'in_progress' && data.code !== 'idempotency_payload_mismatch')) {
            return update('pending', 'No se recibió confirmación del cierre. Se reintentará la misma intención.', { incrementRetry: true });
        }
        const knownMessage = data.code === 'idempotency_payload_mismatch'
            ? 'El servidor registró otro contenido para esta clave de cierre. Se conserva la intención para revisión.'
            : data.error === 'version_conflict' ? 'Los datos del servidor cambiaron. Comprobá el cierre anterior antes de confirmar otro.'
                : data.error === 'finalization_receipt_required' ? 'La visita cambió de estado. Comprobá el recibo del cierre anterior.' : null;
        return update('conflict', knownMessage || data.message || data.error || 'El cierre requiere revisión. La intención original se conserva.');
    }
    if (!isCurrent()) return { status: 'stale' };
    const data = response?.data || response || {};
    if (data.ignored === true || data.conflict === true || data.error === 'version_conflict') {
        return update('conflict', data.message || data.error || 'El servidor no aplicó el cierre. Revisá la visita antes de continuar.');
    }
    const confirmed = (data.success === true || data.status === 'applied' || data.idempotent === true)
        && data.applied_operation_uuid === uuid && response?._httpStatus !== 202
        && (payload.finalization_contract !== 2 || (data.finalization_effect === 'finalized' && Number(data.visita_id) === Number(claimed.visitaId)
            && Number(data.empresa_id) === Number(payload.owner_empresa_id)
            && (empresaId === undefined || Number(data.empresa_id) === Number(empresaId))));
    if (!confirmed) {
        return update('pending', 'El servidor no confirmó esta intención de cierre. Se conserva para reintentar.');
    }
    let removed;
    try {
        removed = payload.finalization_contract === 2
            ? await getOfflineStorageDriver().commitVisitaFinalization(claimed, data, isCurrent)
            : await removeVisitaOfflineOperation(claimed.id, { ownerId, claimToken: claimed.claimToken });
    } catch (error) {
        if (payload.finalization_contract !== 2) throw error;
        return update('pending', 'El servidor confirmó el cierre, pero no se pudo conservar su estado local. Se reintentará la misma intención.');
    }
    if (!removed) return update('pending', 'La copia local cambió durante la confirmación. Se conserva el cierre para reintentar.');
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('visita-finalization-acknowledged', { detail: { userId: claimed.userId, visitaId: claimed.visitaId } }));
    return removed ? { status: 'applied', data } : { status: 'stale' };
}
